import WRAP from "WRAP-JS";
import {
  _orderBy,
  calcPressureFromAlt,
  formatBaseTime,
  formatValidTime,
  ft2m,
  parseUTCDate
} from "../../../utils/FPEUtils";
import {
  configUkmetDh,
  configUkmetSpeedDirectionController,
  configUkmetUpConversionController,
  configUkmetLayerIsotach,
  configUkmetLayerIsotachMesh,
  configUkmetLayerIsotherm,
  configUkmetLayerIsothermMesh,
  configUkmetLayerStreamLine,
  configUkmetLayerIsothermGrid,
  configUkmetLayerWindArrow,
  configUkmetLayerGeoPotentialHeight,
  configUkmetIcingProbController,
  configUkmetLayerIcingProb
} from "./config/ConfigUkmet";
import LegendUkmet from "../legend/LegendUkmet";
import { WX_ID_UKMET } from "../../../store/WeatherContentsState";

class Ukmet {
  static NAME = WX_ID_UKMET;
  static WIND_ARROW_NAME = `${Ukmet.NAME}_WIND_ARROW`;
  static WIND_STREAM_NAME = `${Ukmet.NAME}_WIND_STREAM`;
  static ISOTACH_NAME = `${Ukmet.NAME}_ISOTACH`;
  static ISOTACH_CONTOUR_NAME = `${Ukmet.NAME}_ISOTACH_CONTOUR`;
  static ISOTACH_MESH_NAME = `${Ukmet.NAME}_ISOTACH_MESH`;
  static ISOTHERM_NAME = `${Ukmet.NAME}_ISOTHERM`;
  static ISOTHERM_CONTOUR_NAME = `${Ukmet.NAME}_ISOTHERM_CONTOUR`;
  static ISOTHERM_MESH_NAME = `${Ukmet.NAME}_ISOTHERM_MESH`;
  static ISOTHERM_GRID_NAME = `${Ukmet.NAME}_ISOTHERM_VALUE`;
  static GEO_POTENTIAL_HEIGHT_NAME = `${Ukmet.NAME}_GEO_POTENTIAL_HEIGHT`;
  static ICING_PROB_NAME = `${Ukmet.NAME}_ICING_PROB_NAME`;

  contentsVisibleState = {
    [Ukmet.WIND_ARROW_NAME]: true,
    [Ukmet.WIND_STREAM_NAME]: false,
    [Ukmet.ISOTACH_NAME]: true,
    [Ukmet.ISOTACH_CONTOUR_NAME]: true,
    [Ukmet.ISOTACH_MESH_NAME]: true,
    [Ukmet.ISOTHERM_NAME]: true,
    [Ukmet.ISOTHERM_CONTOUR_NAME]: true,
    [Ukmet.ISOTHERM_MESH_NAME]: true,
    [Ukmet.ISOTHERM_GRID_NAME]: true,
    [Ukmet.GEO_POTENTIAL_HEIGHT_NAME]: true,
    [Ukmet.ICING_PROB_NAME]: true,
  };

  dh;
  dhSpeedDirectionController;
  dhUpConversionController;
  dhIcingProbController;
  ready = false;
  layerWindArrow;
  layerStream;
  layerIsotach;
  layerIsotachMesh;
  layerIsotherm;
  layerIsothermMesh;
  layerIsothermGrid;
  layerGeoPotentialHeight;
  layerIcingProb;
  etd;
  elapsed = 0;
  baseTime = '---';
  validTime = '---';
  altitude = 0;
  level;
  dispatcher;
  visible;

  constructor() {
    this.layerWindArrow = new WRAP.Geo.Layer(Ukmet.WIND_ARROW_NAME);
    this.layerStream = new WRAP.Geo.Layer(Ukmet.WIND_STREAM_NAME);
    this.layerIsotach = new WRAP.Geo.Layer(Ukmet.ISOTACH_CONTOUR_NAME);
    this.layerIsotachMesh = new WRAP.Geo.Layer(Ukmet.ISOTACH_MESH_NAME);
    this.layerIsotherm = new WRAP.Geo.Layer(Ukmet.ISOTHERM_CONTOUR_NAME);
    this.layerIsothermMesh = new WRAP.Geo.Layer(Ukmet.ISOTHERM_MESH_NAME);
    this.layerIsothermGrid = new WRAP.Geo.Layer(Ukmet.ISOTHERM_GRID_NAME);
    this.layerGeoPotentialHeight = new WRAP.Geo.Layer(Ukmet.GEO_POTENTIAL_HEIGHT_NAME);
    this.layerIcingProb = new WRAP.Geo.Layer(Ukmet.ICING_PROB_NAME);
    this.layerWindArrow.setVisible(false);
    this.layerStream.setVisible(false);
    this.layerIsotach.setVisible(false);
    this.layerIsotachMesh.setVisible(false);
    this.layerIsotherm.setVisible(false);
    this.layerIsothermMesh.setVisible(false);
    this.layerIsothermGrid.setVisible(false);
    this.layerGeoPotentialHeight.setVisible(false);
    this.layerIcingProb.setVisible(false);
    this.layerWindArrow.disableInteraction(true);
    this.layerStream.disableInteraction(true);
    this.layerIsotach.disableInteraction(true);
    this.layerIsotachMesh.disableInteraction(true);
    this.layerIsotherm.disableInteraction(true);
    this.layerIsothermMesh.disableInteraction(true);
    this.layerIsothermGrid.disableInteraction(true);
    this.layerGeoPotentialHeight.disableInteraction(true);
    this.layerIcingProb.disableInteraction(true);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.ready = true;
  }

  initData() {
    return new Promise(resolve => {
      this.dh = new WRAP.Data(configUkmetDh);
      this.dh.updated('timelist', (ref) => {
        resolve();
        this._selectBaseValidTime(ref);
      });

      this.dhSpeedDirectionController = new WRAP.Data(configUkmetSpeedDirectionController);
      this.dhUpConversionController = new WRAP.Data(configUkmetUpConversionController);
      this.dhIcingProbController = new  WRAP.Data(configUkmetIcingProbController);
    });
  }

  initLayer() {
    this.layerWindArrow.configure(this.dh, configUkmetLayerWindArrow);
    this.layerStream.configure(this.dh, configUkmetLayerStreamLine);
    this.layerIsotach.configure(this.dhSpeedDirectionController, configUkmetLayerIsotach);
    this.layerIsotachMesh.configure(this.dhSpeedDirectionController, configUkmetLayerIsotachMesh);
    this.layerIsotherm.configure(this.dhUpConversionController, configUkmetLayerIsotherm);
    this.layerIsothermMesh.configure(this.dhUpConversionController, configUkmetLayerIsothermMesh);
    this.layerIsothermGrid.configure(this.dh, configUkmetLayerIsothermGrid);
    this.layerGeoPotentialHeight.configure(this.dhUpConversionController, configUkmetLayerGeoPotentialHeight);
    this.layerIcingProb.configure(this.dhIcingProbController, configUkmetLayerIcingProb);

    this.layerWindArrow.setVisible(this.contentsVisibleState[Ukmet.WIND_ARROW_NAME]);
    this.layerStream.setVisible(this.contentsVisibleState[Ukmet.WIND_STREAM_NAME]);
    this.layerIsotach.setVisible(this.contentsVisibleState[Ukmet.ISOTACH_CONTOUR_NAME])
    this.layerIsotachMesh.setVisible(this.contentsVisibleState[Ukmet.ISOTACH_MESH_NAME])
    this.layerIsotherm.setVisible(this.contentsVisibleState[Ukmet.ISOTHERM_CONTOUR_NAME]);
    this.layerIsothermMesh.setVisible(this.contentsVisibleState[Ukmet.ISOTHERM_MESH_NAME]);
    this.layerIsothermGrid.setVisible(this.contentsVisibleState[Ukmet.ISOTHERM_GRID_NAME]);
    this.layerGeoPotentialHeight.setVisible(this.contentsVisibleState[Ukmet.GEO_POTENTIAL_HEIGHT_NAME]);
    this.layerIcingProb.setVisible(this.contentsVisibleState[Ukmet.ICING_PROB_NAME]);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  getLayer() {
    return this._allLayers();
  }

  setVisible(visible, contents) {

    if (!this.ready) return;

    let target;
    switch (contents) {
      case Ukmet.WIND_ARROW_NAME:
        target = [this.layerWindArrow];
        break;
      case Ukmet.WIND_STREAM_NAME:
        target = [this.layerStream];
        break;
      case Ukmet.ISOTACH_NAME:
        target = [this.layerIsotach, this.layerIsotachMesh];
        break;
      case Ukmet.ISOTACH_CONTOUR_NAME:
        target = [this.layerIsotach];
        break;
      case Ukmet.ISOTACH_MESH_NAME:
        target = [this.layerIsotachMesh];
        break;
      case Ukmet.ISOTHERM_NAME:
        target = [this.layerIsotherm, this.layerIsothermMesh, this.layerIsothermGrid];
        break;
      case Ukmet.ISOTHERM_CONTOUR_NAME:
        target = [this.layerIsotherm];
        break;
      case Ukmet.ISOTHERM_MESH_NAME:
        target = [this.layerIsothermMesh];
        break;
      case Ukmet.ISOTHERM_GRID_NAME:
        target = [this.layerIsothermGrid];
        break;
      case Ukmet.GEO_POTENTIAL_HEIGHT_NAME:
        target = [this.layerGeoPotentialHeight];
        break;
      case Ukmet.ICING_PROB_NAME:
        target = [this.layerIcingProb];
        break;
      default:
        target = this._allLayers();
    }

    if (contents) {
      this.contentsVisibleState[contents] = visible;
    }

    if (visible) {
      target.forEach(layer => {
        const layerName = layer.name();
        if (this.contentsVisibleState[layerName]) {
          if (layerName.includes(Ukmet.ISOTACH_NAME)) {
            if (this.contentsVisibleState[Ukmet.ISOTACH_NAME]) {
              layer.show();
            }
          } else if (layerName.includes(Ukmet.ISOTHERM_NAME) ) {
            if (this.contentsVisibleState[Ukmet.ISOTHERM_NAME]) {
              layer.show();
            }
          } else {
            layer.show();
          }
        }
      });
    } else {
      target.forEach(layer => {layer.hide()})
    }

    if (contents === undefined) {
      this.dh.setAutoUpdate('timelist', visible);
      this.dhSpeedDirectionController.setAutoUpdate('timelist', visible);
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }
  }

  getTimeInformation() {
    const baseTime = this.baseTime !== '---' ? formatBaseTime(this.baseTime) : this.baseTime;
    const validTime = this.validTime !== '---' ? formatValidTime(this.validTime) : this.validTime;
    return {
      id: Ukmet.NAME,
      label: 'UKMET',
      baseTime: baseTime,
      validTime: validTime
    };
  }

  getLegend() {
    return {
      id: Ukmet.NAME,
      label: 'UKMET',
      component: LegendUkmet,
      contents: this
    };
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  getIndividualAltitude() {
    return {
      id: Ukmet.NAME,
      altitude: this.altitude,
      label: 'UKMET',
      tooltip: this._altitudeTooltip
    };
  }

  getAltitudeLabel() {
    return this._altitudeTooltip(this.altitude);
  }

  applyIndividualAltitude(altitude) {
    if (this.altitude !== altitude) {
      this.altitude = altitude;
      const level = this._getUkmetLevelByAltitude(this.altitude) + '';
      if (this.level !== level) {
        this._updateDataset();
      }
    }
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dh.retained.timelist);
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this.ready) return;

    this._selectBaseValidTime(this.dh.retained.timelist);
  }

  _selectBaseValidTime(ref) {
    let timeList = ref;
    if (!timeList) return;
    timeList = timeList.timelist;

    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      targetTime = Date.now();
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    //最新のbasetimeを取得
    const latestBasetime = _orderBy(timeList, ['basetime'], ['desc'])[0];
    const tmpTimeList = latestBasetime.validtime.map(validtime => {
      return {
        "validtime": validtime,
        "basetime": latestBasetime.basetime,
        "validtime_msec": parseUTCDate(validtime).getTime()
      };
    });

    // targetTimeに最も近いvalidを選択する(直近が過去時制と未来時制で同時刻の場合、未来時制のValidデータを採用する。)
    const sortedTimelist = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimelist[0];

    if (selectedTime && this.validTime !== selectedTime.validtime) {
      this.baseTime = selectedTime.basetime;
      this.validTime = selectedTime.validtime;
      this._updateDataset();
    }
  }

  _updateDataset() {
    if (this.baseTime !== '---' && this.validTime !== '---') {
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      this.level = this._getUkmetLevelByAltitude(this.altitude) + '';

      this.layerWindArrow.set({
        element: ['UGRD', 'VGRD'],
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerStream.set({
        element: ['UGRD', 'VGRD'],
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerIsotach.set({
        element: "Speed",
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerIsotachMesh.set({
        element: "Speed",
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerIsotherm.set({
        element: 'TMP',
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerIsothermMesh.set({
        element: 'TMP',
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerIsothermGrid.set({
        element: 'TMP',
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerGeoPotentialHeight.set({
        element: 'HGT',
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });

      this.layerIcingProb.set({
        element: 'IcingProb',
        level: this.level,
        basetime: this.baseTime,
        validtime: this.validTime
      });
    }
  }

  _getUkmetLevelByAltitude(altitude) {
    const ukmet_mb = [100, 125, 150, 175, 200, 225, 250, 275, 300, 350, 400, 450, 500, 600, 700, 750, 850];
    //flightLevel → meter → mb
    const m = ft2m(altitude * 100);
    const mb = calcPressureFromAlt(m);
    const near = _orderBy(ukmet_mb,[
      o => {
        return Math.abs(mb - o);
      },
      o => {
        return o;
      }
    ])
    return near[0];
  }

  _altitudeTooltip(altitude) {
    const ukmet = [{fl: 530, mb: 100},
      {fl: 480, mb: 125},
      {fl: 450, mb: 150},
      {fl: 410, mb: 175},
      {fl: 390, mb: 200},
      {fl: 360, mb: 225},
      {fl: 340, mb: 250},
      {fl: 320, mb: 275},
      {fl: 300, mb: 300},
      {fl: 270, mb: 350},
      {fl: 240, mb: 400},
      {fl: 210, mb: 450},
      {fl: 180, mb: 500},
      {fl: 140, mb: 600},
      {fl: 100, mb: 700},
      {fl: 80, mb: 750},
      {fl: 50, mb: 850}];

    const m = ft2m(altitude * 100);
    const mb = calcPressureFromAlt(m);
    const near = _orderBy(ukmet, [
      o => {
        return Math.abs(mb - o.mb);
      },
      o => {
        return o;
      }
    ])[0];
    const fl = ('000' + near.fl).slice(-3);
    return `FL${fl} / ${near.mb}hPa`;
  }

  _allLayers() {
    return [
      this.layerIsothermMesh,
      this.layerIsotherm,
      this.layerIsothermGrid,
      this.layerGeoPotentialHeight,
      this.layerIsotachMesh,
      this.layerIsotach,
      this.layerIcingProb,
      this.layerWindArrow,
      this.layerStream
    ];
  }

  applyMapColor(mapColor) {
    const style = mapColor === "white" ? "default" : "dark";
    this.layerIsothermGrid.setStyle(style);
    this.layerIcingProb.setStyle(style);
  }
}

export default Ukmet;